'use client';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { FALLBACK_FEATURE_FLAGS } from './constants';
import type { FeatureFlags } from './types';
import { coerceIntoBoolean } from './utils';

type PlatformInterfaceFlags = InterfaceEntity['properties']['flags'];

const FlagsContext = createContext<(PlatformInterfaceFlags & FeatureFlags & {}) | null>(null);

export function useFlags() {
  const context = useContext(FlagsContext);
  if (context === null) {
    throw new Error('FlagsContext must be used within a FlagsProvider');
  }
  return context;
}

export default function FlagsProvider({
  flags = {},
  featureFlags = FALLBACK_FEATURE_FLAGS,
  children,
}: {
  flags?: PlatformInterfaceFlags;
  featureFlags?: FeatureFlags;
  children: ReactNode;
}) {
  const { showBreadcrumbsInEmbeddedApp = false, enableAutomationTemplates = true } = flags;

  const {
    enable_save_and_close = 'false',
    fetch_runs_from_connected_env = 'false',
    enable_import_export_changeset = 'false',
    enable_automation_documentation = 'false',
    disable_knowledge_reporting = 'false',
    default_sensitive_value = 'false',
    INTERFACE_COLLABORATION = 'false',
    enable_ai_create_interface = 'false',
    enable_text_to_interface_code = 'false',
    enable_text_to_code_org_only = 'false',
    enable_ai_create_workflow = 'false',
    enable_ai_create_agent = 'false',
    enable_ai_fde = 'false',
    enable_cm_variable_editor = 'true',
    enable_support_portal = 'false',
  } = featureFlags;

  const context = useMemo(
    () => ({
      showBreadcrumbsInEmbeddedApp,
      enableAutomationTemplates,
      enable_save_and_close: coerceIntoBoolean(enable_save_and_close),
      fetch_runs_from_connected_env: coerceIntoBoolean(fetch_runs_from_connected_env),
      enable_import_export_changeset: coerceIntoBoolean(enable_import_export_changeset),
      enable_automation_documentation: coerceIntoBoolean(enable_automation_documentation),
      disable_knowledge_reporting: coerceIntoBoolean(disable_knowledge_reporting),
      default_sensitive_value: coerceIntoBoolean(default_sensitive_value),
      INTERFACE_COLLABORATION: coerceIntoBoolean(INTERFACE_COLLABORATION),
      enable_ai_create_interface: coerceIntoBoolean(enable_ai_create_interface),
      enable_text_to_interface_code: coerceIntoBoolean(enable_text_to_interface_code),
      enable_text_to_code_org_only: coerceIntoBoolean(enable_text_to_code_org_only),
      enable_ai_create_workflow: coerceIntoBoolean(enable_ai_create_workflow),
      enable_ai_create_agent: coerceIntoBoolean(enable_ai_create_agent),
      enable_ai_fde: coerceIntoBoolean(enable_ai_fde),
      enable_cm_variable_editor: coerceIntoBoolean(enable_cm_variable_editor),
      enable_support_portal: coerceIntoBoolean(enable_support_portal),
    }),
    [
      showBreadcrumbsInEmbeddedApp,
      enableAutomationTemplates,
      enable_save_and_close,
      fetch_runs_from_connected_env,
      enable_import_export_changeset,
      enable_automation_documentation,
      disable_knowledge_reporting,
      default_sensitive_value,
      INTERFACE_COLLABORATION,
      enable_ai_create_interface,
      enable_text_to_interface_code,
      enable_text_to_code_org_only,
      enable_ai_create_workflow,
      enable_ai_create_agent,
      enable_ai_fde,
      enable_cm_variable_editor,
      enable_support_portal,
    ],
  );

  return <FlagsContext.Provider value={context}>{children}</FlagsContext.Provider>;
}
